import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  GitCommit,
  Layers,
  ShieldAlert,
  Sliders,
  Smartphone,
  ChevronLeft,
  ChevronRight,
  Server,
  Settings,
  LogOut,
} from 'lucide-react';
import { useTelemetry } from '../context/TelemetryContext';
import logo from '../assets/cestasoft-logo.png';
import { performFederatedLogout } from '../services/ssoAuth';

export default function Sidebar({
  activeView,
  setActiveView,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isCollapsed, setIsCollapsed, isLiveBackend, forceMock } = useTelemetry();

  const menuItems = [
    { id: 'overview', path: '/dashboard', label: 'Executive Overview', icon: LayoutDashboard, badge: null },
    { id: 'funnels', path: '/funnels', label: 'Flow & Funnels', icon: GitCommit, badge: 'Conversion' },
    { id: 'menus', path: '/menus', label: 'Menu Analytics', icon: Layers, badge: null },
    { id: 'errors', path: '/errors', label: 'Errors & Friction', icon: ShieldAlert, badge: 'Issues' },
    { id: 'configs', path: '/configs', label: 'MNO Gateways', icon: Sliders, badge: 'Rules' },
    { id: 'simulator', path: '/simulator', label: 'USSD Sandbox', icon: Smartphone, badge: 'Test Lab' },
    { id: 'setting', path: '/setting', label: 'Settings', icon: Settings, badge: null },
  ];

  const handleNavigation = (item) => {
    if (setActiveView) {
      setActiveView(item.id);
    }
    navigate(item.path);
  };

  const handleLogout = () => {
    performFederatedLogout();
  };

  const currentPath = location?.pathname || '/dashboard';

  return (
    <aside style={{
      width: isCollapsed ? '80px' : '260px',
      background: '#0B0F19',
      borderRight: '1px solid #1E293B',
      display: 'flex',
      flexDirection: 'column',
      transition: 'width 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
      zIndex: 50,
      minHeight: '100vh',
      position: 'sticky',
      top: 0,
      flexShrink: 0,
      boxShadow: '4px 0 24px rgba(0, 0, 0, 0.08)'
    }}>
      
      {/* Brand Header */}
      <div style={{
        padding: '24px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: isCollapsed ? 'center' : 'space-between',
        borderBottom: '1px solid #1E293B'
      }}>
        <div 
          style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', overflow: 'hidden' }} 
          onClick={() => navigate('/dashboard')}
        >
          <div style={{
            background: '#FFFFFF',
            borderRadius: '8px',
            padding: '6px 8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            flexShrink: 0
          }}>
            <img 
              src={logo} 
              alt="CestaSoft Logo" 
              style={{ height: '22px', width: 'auto', display: 'block', objectFit: 'contain' }} 
            />
          </div>
          {!isCollapsed && (
            <div>
              <div style={{ fontSize: '15px', fontWeight: '800', letterSpacing: '-0.02em', color: '#FFFFFF' }}>
                CestaConnect
              </div>
              <div style={{ fontSize: '11px', color: '#94A3B8' }}>USSD Telemetry</div>
            </div>
          )}
        </div>

        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          style={{
            background: 'rgba(255, 255, 255, 0.06)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '6px',
            padding: '5px',
            color: '#94A3B8',
            cursor: 'pointer',
            display: isCollapsed ? 'none' : 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          title={isCollapsed ? 'Expand' : 'Collapse'}
        >
          <ChevronLeft size={16} />
        </button>
      </div>

      {isCollapsed && (
        <div style={{ padding: '8px', display: 'flex', justifyContent: 'center' }}>
          <button
            onClick={() => setIsCollapsed(false)}
            style={{
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '6px',
              padding: '6px',
              color: '#94A3B8',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Expand Sidebar"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Navigation Links */}
      <nav style={{ padding: '16px 12px', display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
        {menuItems.map(item => {
          const Icon = item.icon;
          const isActive = currentPath === item.path || (currentPath === '/' && item.path === '/dashboard') || activeView === item.id;

          return (
            <button
              key={item.id}
              onClick={() => handleNavigation(item)}
              title={isCollapsed ? item.label : undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: isCollapsed ? 'center' : 'space-between',
                padding: '12px 14px',
                borderRadius: '8px',
                background: isActive ? 'linear-gradient(135deg, #F84122 0%, #E65100 100%)' : 'transparent',
                border: 'none',
                color: isActive ? '#FFFFFF' : '#94A3B8',
                fontWeight: isActive ? '700' : '500',
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                width: '100%',
                textAlign: 'left',
                boxShadow: isActive ? '0 4px 12px rgba(248, 65, 34, 0.35)' : 'none'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Icon size={18} color={isActive ? '#FFFFFF' : '#94A3B8'} />
                {!isCollapsed && <span>{item.label}</span>}
              </div>

              {!isCollapsed && item.badge && (
                <span style={{
                  fontSize: '10px',
                  padding: '2px 7px',
                  borderRadius: '999px',
                  background: isActive ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.08)',
                  color: '#FFFFFF',
                  fontWeight: '700'
                }}>
                  {item.badge}
                </span>
              )}
            </button>
          );
        })}

        {/* Logout Button */}
        <div style={{ marginTop: 'auto', paddingTop: '12px' }}>
          <button
            onClick={handleLogout}
            title={isCollapsed ? 'Sign Out' : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              padding: '12px 14px',
              borderRadius: '8px',
              background: 'rgba(244, 63, 94, 0.1)',
              border: '1px solid rgba(244, 63, 94, 0.25)',
              color: '#F87171',
              fontWeight: '600',
              fontSize: '13px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              width: '100%',
              gap: '12px',
            }}
          >
            <LogOut size={18} color="#F87171" />
            {!isCollapsed && <span>Sign Out</span>}
          </button>
        </div>
      </nav>

      {/* Footer System Status */}
      <div style={{
        padding: '16px',
        borderTop: '1px solid #1E293B',
        background: 'rgba(0, 0, 0, 0.3)'
      }}>
        {!isCollapsed ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Server size={13} color="#94A3B8" />
              <span style={{ fontSize: '11px', color: '#94A3B8', fontWeight: '600' }}>Engine: Port 8145</span>
            </div>
            <div className={`live-indicator ${isLiveBackend && !forceMock ? 'online' : 'mock'}`} style={{ width: 'fit-content' }}>
              <span className="dot" />
              <span>{isLiveBackend && !forceMock ? 'Live MongoDB' : 'Simulated Data'}</span>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <span className={`dot ${isLiveBackend && !forceMock ? 'online' : 'mock'}`} style={{ width: '8px', height: '8px', borderRadius: '50%', background: isLiveBackend && !forceMock ? 'var(--accent-emerald)' : 'var(--accent-orange)' }} />
          </div>
        )}
      </div>

    </aside>
  );
}
